import React, { useMemo } from "react";
import { Box, FormControl, Typography, TextField, MenuItem } from "@mui/material";

// Let long company/vendor names wrap onto a new line instead of being
// truncated/overflowing on narrow (mobile) widths.
const wrappingSelectSx = {
  "& .MuiSelect-select": {
    whiteSpace: "normal",
    wordBreak: "break-word",
    display: "flex",
    alignItems: "center",
  },
};
const wrappingMenuItemSx = { whiteSpace: "normal", wordBreak: "break-word" };

export default function OrderFormSelectors({
  date,
  onDateChange,
  cluster,
  onClusterChange,
  clientId,
  onClientChange,
  clients,
  clientsLoading,
  vendorId,
  onVendorChange,
  vendorOptions,
  orderPlacedByClientId = {},
}) {
  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.name.localeCompare(b.name)),
    [clients],
  );

  return (
    <>
      {/* Date */}
      <FormControl fullWidth sx={{ mt: 2 }}>
        <Typography variant="subtitle2">
         Service Date <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextField type="date" size="small" fullWidth value={date} onChange={onDateChange} />
      </FormControl>

      {/* Cluster */}
      <FormControl fullWidth sx={{ mt: 2 }}>
        <Typography variant="subtitle2">
          Cluster <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextField select size="small" fullWidth value={cluster} onChange={onClusterChange}>
          <MenuItem value="cluster1">Cluster 1</MenuItem>
          <MenuItem value="cluster2">Cluster 2</MenuItem>
        </TextField>
      </FormControl>

      {/* Company */}
      <FormControl fullWidth sx={{ mt: 2 }}>
        <Typography variant="subtitle2">
          Company Name <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextField
          select
          size="small"
          fullWidth
          value={clientId}
          onChange={onClientChange}
          disabled={clientsLoading}
          sx={wrappingSelectSx}
          SelectProps={{
            renderValue: (selected) => {
              const client = clients.find((c) => c._id === selected);
              if (!client) return "";
              return `${client.name}${client.location ? ` — ${client.location}` : ""}`;
            },
          }}
        >
          <MenuItem value="">
            <em>Select company</em>
          </MenuItem>
          {sortedClients.map((c) => {
            const orderPlaced = !!orderPlacedByClientId[c._id];
            return (
              <MenuItem
                key={c._id}
                value={c._id}
                sx={{ ...wrappingMenuItemSx, flexDirection: "column", alignItems: "flex-start" }}
              >
                <Box component="span" sx={{ color: orderPlaced ? "text.disabled" : "text.primary" }}>
                  {c.name} {c.location ? `— ${c.location}` : ""}
                </Box>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: orderPlaced ? "text.secondary" : "#000", fontWeight: orderPlaced ? 400 : 600 }}
                >
                  ({orderPlaced ? "Count Updated" : "Pending"})
                </Typography>
              </MenuItem>
            );
          })}
        </TextField>
      </FormControl>

      {/* Vendor */}
      {clientId && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <Typography variant="subtitle2">
            Vendor <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={vendorId}
            onChange={onVendorChange}
            disabled={vendorOptions.length === 0}
            sx={wrappingSelectSx}
          >
            <MenuItem value="">
              <em>{vendorOptions.length ? "Select vendor" : "No vendor on file"}</em>
            </MenuItem>
            {vendorOptions.map((v) => (
              <MenuItem key={v._id} value={v._id} sx={wrappingMenuItemSx}>
                {v.name}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      )}
    </>
  );
}
